import React from "react";
import { GridNode, HoverElement } from "../../types";
import { HourlyDispatchResult } from "../../powerFlow/multiTimestepOptimization";
import KeyIcon from "./KeyIcon";
import ColumnHeader from "./ColumnHeader";

interface Props {
  batteries: GridNode[];
  batteryStates?: Map<string, { soc: number; power: number }>;
  hourlyGridStates: HourlyDispatchResult[];
  onBatteryParametersChange?: (
    batteryId: string,
    params: {
      maxChargeMW?: number;
      maxDischargeMW?: number;
      maxEnergyMWh?: number;
      minEnergyMWh?: number;
      efficiency?: number;
      initialEnergyMWh?: number;
    }
  ) => void;
  onCommitEdit?: () => void;
  requireMatchingSOC?: boolean;
  onRequireMatchingSOCChange?: (value: boolean) => void;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  hoverHour?: number | null;
  onHourHover?: (hour: number | null) => void;
  numTimeSteps: number;
}

const BatteryTable: React.FC<Props> = ({
  batteries,
  batteryStates,
  hourlyGridStates,
  onBatteryParametersChange,
  onCommitEdit,
  requireMatchingSOC = false,
  onRequireMatchingSOCChange,
  hoverElement,
  onElementHover,
  hoverHour,
  onHourHover,
  numTimeSteps,
}) => {
  
  const isHovered = (batteryId: string) => {
    return hoverElement?.type === "Node" && hoverElement?.id === batteryId;
  };


  const renderParametersTab = () => (
    <>
      <div style={{ marginBottom: "12px", padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
        <label style={{ display: "flex", alignItems: "center", fontSize: "13px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={requireMatchingSOC}
            onChange={(e) => onRequireMatchingSOCChange?.(e.target.checked)}
            style={{ marginRight: "8px" }}
          />
          Require ending SoC to equal starting SoC
        </label>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #ddd" }}>
            <ColumnHeader column="id" style={{ textAlign: "left", padding: "8px" }}>ID<KeyIcon /></ColumnHeader>
            <ColumnHeader column="max_charge" style={{ textAlign: "right", padding: "8px" }}>Max Charge (MW)</ColumnHeader>
            <ColumnHeader column="max_discharge" style={{ textAlign: "right", padding: "8px" }}>Max Discharge (MW)</ColumnHeader>
            <ColumnHeader column="max_energy" style={{ textAlign: "right", padding: "8px" }}>Capacity (MWh)</ColumnHeader>
            <ColumnHeader column="min_energy" style={{ textAlign: "right", padding: "8px" }}>Min Energy (MWh)</ColumnHeader>
            <ColumnHeader column="efficiency" style={{ textAlign: "right", padding: "8px" }}>Efficiency</ColumnHeader>
            <ColumnHeader column="initial_soc" style={{ textAlign: "right", padding: "8px" }}>Initial (MWh)</ColumnHeader>
            <th style={{ textAlign: "right", padding: "8px" }}>State of Charge (MWh)</th>
            <th style={{ textAlign: "right", padding: "8px" }}>Power (MW)</th>
          </tr>
        </thead>
        <tbody>
          {batteries.map((battery) => (
            <tr
              key={battery.id}
              style={{
                borderBottom: "1px solid #eee",
                background: isHovered(battery.id) ? "#e3f2fd" : "transparent",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={() => onElementHover({ type: "Node", id: battery.id })}
              onMouseLeave={() => onElementHover(null)}
            >
              <td style={{ padding: "8px", fontWeight: "bold" }}>{battery.id}</td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.maxChargeMW || 100}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      maxChargeMW: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="10"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.maxDischargeMW || 100}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      maxDischargeMW: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="10"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.maxEnergyMWh || 500}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      maxEnergyMWh: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="50"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.minEnergyMWh || 0}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      minEnergyMWh: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="10"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.efficiency || 0.9}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      efficiency: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="0.05"
                  min="0"
                  max="1"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                <input
                  type="number"
                  value={battery.initialEnergyMWh || 250}
                  onChange={(e) =>
                    onBatteryParametersChange?.(battery.id, {
                      initialEnergyMWh: parseFloat(e.target.value),
                    })
                  }
                  onBlur={onCommitEdit}
                  style={{
                    width: "80px",
                    padding: "4px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                  step="10"
                />
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                {batteryStates?.get(battery.id)?.soc?.toFixed(1) || "-"}
              </td>
              <td style={{ textAlign: "right", padding: "8px" }}>
                {battery.value ? battery.value.toFixed(1) : "0.0"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <div>
      {renderParametersTab()}
      
      {batteries.length === 0 && (
        <p style={{ color: "#999", textAlign: "center", marginTop: "20px" }}>
          No batteries in the system
        </p>
      )}
    </div>
  );
};

export default BatteryTable;